import { useQuery } from "@tanstack/react-query";
import { GET_MY_ENROLLMENTS } from "../api/queries";
import { graphQLClient } from "../graphqlClient";
import { useReactQueryErrorReporter } from "./use-error-reporter";
import type { MyEnrollment } from "../types/enrollments";

export type { MyEnrollment };

interface GetMyEnrollmentsResponse {
  getMyEnrollments: MyEnrollment[];
}

/**
 * Loads the signed-in user's enrollments — one entry per `enr-` record — each
 * resolved to its scope (section/school/district), the scoped entity's id, and
 * that entity's name. Use this instead of parsing section/school ids out of the
 * user's record path, which is empty under the enrollment data model.
 */
export const useMyEnrollments = () => {
  const { data, error, isLoading, refetch } = useQuery<MyEnrollment[]>({
    queryKey: ["myEnrollments"],
    queryFn: async () => {
      const resp =
        await graphQLClient.request<GetMyEnrollmentsResponse>(
          GET_MY_ENROLLMENTS,
        );
      return resp.getMyEnrollments ?? [];
    },
  });

  useReactQueryErrorReporter("Could not load your enrollments", error, false);

  return { enrollments: data ?? [], loading: isLoading, error, refetch };
};
